import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { Dialog } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import PriceCut from "../PriceCut";
import useMediaQuery from "../../hooks/useMediaQuery";
import { getOptimizedUrl } from "../../utils/cloudinaryOptimize";
import {
  chosenDishOptions,
  dishOptionsComplete,
  dishSelectionFromOptions,
  toggleDishOption,
} from "../../utils/dishOptions";
import { MAX_LINE_QTY, formatBasketMoney } from "../../utils/groupBasketView";
import {
  Choice,
  ChoiceGrid,
  GREEN,
  IconCircle,
  MUTED,
  PrimaryButton,
  SheetBody,
  SheetFoot,
  SheetHead,
  TEXT,
  sheetPaperProps,
} from "./groupBasketStyles";

const plainText = (html) =>
  String(html || "")
    .replace(/<[^>]+>/g, " ")
    .replace(/&nbsp;/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const groupRule = (group) => {
  const min = Number(group.minOptions) || 0;
  const max = Number(group.maxOptions) || 0;
  if (min > 0 && max === min) return `Choose ${min}`;
  if (min > 0 && max > min) return `Choose ${min} to ${max}`;
  if (min > 0) return `Choose at least ${min}`;
  if (max > 0) return `Optional, up to ${max}`;
  return "Optional";
};

/*
 * One dish, chosen for the caller's own lines in an Order together basket.
 * The same option rules as the restaurant dish dialog (utils/dishOptions),
 * without the restaurant cart: saving hands { productId, qty, options } to
 * the basket, which the server prices. The dish's menu price is shown as the
 * menu lists it; the line total comes back from the server.
 */
const GroupDishDialog = ({ open, product, line = null, currency, busy, onSave, onClose }) => {
  const fullScreen = useMediaQuery("(max-width: 600px)");
  const [quantity, setQuantity] = useState(1);
  const [selected, setSelected] = useState({});

  useEffect(() => {
    if (!product) return;
    setQuantity(Number(line?.qty) || 1);
    setSelected(dishSelectionFromOptions(product, line?.options));
  }, [product, line]);

  if (!product) return null;

  const money = (amount) => formatBasketMoney(amount, currency);
  const complete = dishOptionsComplete(product, selected);
  const description = plainText(product.description);
  const editing = Boolean(line);

  const save = () => {
    if (!complete || busy) return;
    onSave({
      productId: product._id,
      qty: quantity,
      options: chosenDishOptions(selected).map((option) => ({ _id: option._id })),
      selectedSpecs: line?.selectedSpecs || {},
    });
  };

  return (
    <Dialog
      open={Boolean(open)}
      onClose={busy ? undefined : onClose}
      fullScreen={fullScreen}
      fullWidth
      maxWidth="sm"
      PaperProps={fullScreen ? undefined : sheetPaperProps}
    >
      <SheetHead>
        <div>
          <h2>{product.name}</h2>
          <p>
            <PriceCut
              price={product.price}
              listPrice={product.listPrice}
              salePrice={product.salePrice}
              format={money}
            />
          </p>
        </div>
        <IconCircle type="button" onClick={onClose} aria-label="Close" disabled={busy}>
          <CloseIcon />
        </IconCircle>
      </SheetHead>
      <SheetBody>
        {product.image ? (
          <Photo src={getOptimizedUrl(product.image, "thumbnail")} alt="" loading="lazy" />
        ) : null}
        {description ? <Description>{description}</Description> : null}

        {(product.optionGroups || []).map((group) => {
          const chosen = selected[group._id] || [];
          return (
            <Group key={group._id}>
              <GroupHead>
                <strong>{group.name}</strong>
                <span>{groupRule(group)}</span>
              </GroupHead>
              <ChoiceGrid $columns={2}>
                {(group.options || []).map((option) => {
                  const isChosen = chosen.some((item) => item._id === option._id);
                  return (
                    <Choice
                      key={option._id}
                      type="button"
                      $selected={isChosen}
                      aria-pressed={isChosen}
                      onClick={() =>
                        setSelected((current) => toggleDishOption(current, product, group._id, option))
                      }
                    >
                      <strong>{option.name}</strong>
                      {Number(option.price) > 0 ? <span>+ {money(option.price)}</span> : null}
                    </Choice>
                  );
                })}
              </ChoiceGrid>
            </Group>
          );
        })}

        <QuantityRow>
          <strong>How many</strong>
          <Stepper>
            <IconCircle
              type="button"
              aria-label="One less"
              disabled={quantity <= 1}
              onClick={() => setQuantity((q) => Math.max(1, q - 1))}
            >
              <RemoveIcon />
            </IconCircle>
            <output aria-live="polite">{quantity}</output>
            <IconCircle
              type="button"
              aria-label="One more"
              disabled={quantity >= MAX_LINE_QTY}
              onClick={() => setQuantity((q) => Math.min(MAX_LINE_QTY, q + 1))}
            >
              <AddIcon />
            </IconCircle>
          </Stepper>
        </QuantityRow>
      </SheetBody>
      <SheetFoot>
        <PrimaryButton type="button" onClick={save} disabled={!complete || busy}>
          {busy ? "Saving" : !complete ? "Make the required choices" : editing ? "Save changes" : "Add to my items"}
        </PrimaryButton>
      </SheetFoot>
    </Dialog>
  );
};

const Photo = styled.img`
  width: 100%;
  aspect-ratio: 16 / 9;
  object-fit: cover;
  border-radius: 16px;
  background: #f3f7ef;
`;

const Description = styled.p`
  margin: 0;
  font-size: 15px;
  line-height: 1.55;
  color: ${MUTED};
`;

const Group = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const GroupHead = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 12px;

  strong {
    font-size: 16px;
    font-weight: 800;
    color: ${TEXT};
  }

  span {
    font-size: 13px;
    font-weight: 700;
    color: ${GREEN};
  }
`;

const QuantityRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;

  strong {
    font-size: 16px;
    font-weight: 800;
  }
`;

const Stepper = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;

  output {
    min-width: 28px;
    text-align: center;
    font-size: 18px;
    font-weight: 800;
    font-variant-numeric: tabular-nums;
  }

  button:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

export default GroupDishDialog;
